// /licenses — admin license catalog with row-level + bulk actions.
//
// 2026-04-27: added per-row delete, multi-select, and bulk ops
// (delete / suspend / activate). The backend already supports all the
// pieces — DELETE /api/v2/licenses/:id and PATCH ...{status} —
// we just expose them in the UI now that the legacy-license import
// landed and operators need to maintain the catalog.
//
// Bulk model is sequential, not transactional: each row's mutation
// fires individually (the API doesn't have a /licenses/bulk endpoint
// yet). For 29-row imports this is fine; if the catalog ever grows
// past a few hundred we'll revisit with a server-side bulk handler.
// On partial failure we surface a count of successes vs. errors and
// keep the failed rows selected so the operator can retry.
//
// Destructive ops (delete, revoke) ALWAYS go through a confirm
// dialog with the row count + names visible. The dialog also requires
// typing "DELETE" for delete-bulk-of-3-or-more — same posture as the
// audit log flush guard (see audit.tsx).

import { useMemo, useState } from "react";
import { Ban, KeyRound, Loader2, Pause, Play, ShieldX, Trash2, X } from "lucide-react";
import { Navigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { useCurrentUser } from "~/lib/auth";
import {
    type LicenseRecord,
    type LicenseStatus,
    useDeleteLicense,
    useLicenses,
    useUpdateLicense,
} from "~/lib/licenses";
import { useTenants } from "~/lib/tenants";

import { CreateLicenseDialog } from "~/components/CreateLicenseDialog";
import { LicenseDetailDialog } from "~/components/LicenseDetailDialog";
import { TableSkeleton } from "~/components/TableSkeleton";

const STATUS_PILL: Record<LicenseRecord["status"], string> = {
    active: "status-pill--live",
    suspended: "status-pill--paused",
    expired: "status-pill--queued",
    revoked: "status-pill--failed",
};

function quotaSummary(l: LicenseRecord): string {
    const parts: string[] = [];
    if (l.quotaPerDay !== undefined) parts.push(`${l.quotaPerDay.toLocaleString()}/day`);
    if (l.quotaTotal !== undefined) parts.push(`${l.quotaTotal.toLocaleString()} total`);
    if (l.seats !== undefined) parts.push(`${l.seats} seats`);
    if (l.devices !== undefined) parts.push(`${l.devices} devices`);
    return parts.length ? parts.join(" · ") : "unlimited";
}

// ---------------------------------------------------------------------------
// Confirm dialog
// ---------------------------------------------------------------------------

interface ConfirmAction {
    kind: "delete" | "suspend" | "activate";
    licenses: LicenseRecord[];
}

function actionVerb(kind: ConfirmAction["kind"]): string {
    switch (kind) {
        case "delete": return "Delete";
        case "suspend": return "Suspend";
        case "activate": return "Activate";
    }
}
function actionPastTense(kind: ConfirmAction["kind"]): string {
    switch (kind) {
        case "delete": return "deleted";
        case "suspend": return "suspended";
        case "activate": return "activated";
    }
}
function actionTone(kind: ConfirmAction["kind"]): "danger" | "warning" | "success" {
    switch (kind) {
        case "delete": return "danger";
        case "suspend": return "warning";
        case "activate": return "success";
    }
}

function ConfirmDialog({
    action,
    onCancel,
    onConfirm,
    busy,
}: {
    action: ConfirmAction | null;
    onCancel: () => void;
    onConfirm: () => void;
    busy: boolean;
}): React.ReactElement | null {
    const [typed, setTyped] = useState("");
    if (!action) return null;
    const verb = actionVerb(action.kind);
    const tone = actionTone(action.kind);
    const requireConfirmType = action.kind === "delete" && action.licenses.length >= 3;
    const canSubmit = !busy && (!requireConfirmType || typed === "DELETE");

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`${verb} ${action.licenses.length} license${action.licenses.length === 1 ? "" : "s"}`}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(2px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 50,
            }}
            onClick={onCancel}
        >
            <div
                className="surface"
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "min(560px, 96vw)",
                    padding: "var(--space-5)",
                    borderRadius: "var(--radius-lg)",
                    boxShadow: "var(--shadow-3)",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "var(--space-3)" }}>
                    <h2 style={{ margin: 0, fontSize: "var(--font-size-16)", fontWeight: 600 }}>
                        {verb} {action.licenses.length} license{action.licenses.length === 1 ? "" : "s"}?
                    </h2>
                    <button type="button" className="btn btn--ghost" onClick={onCancel} aria-label="Close" style={{ padding: 4 }}>
                        <X size={16} />
                    </button>
                </div>

                <p className="text-text-muted" style={{ fontSize: "var(--font-size-13)", marginTop: 0 }}>
                    {action.kind === "delete" && (
                        <>This will <b style={{ color: "var(--color-danger)" }}>permanently remove</b> the license rows and cascade to any attached API keys + legacy-license bindings. It cannot be undone.</>
                    )}
                    {action.kind === "suspend" && (
                        <>Suspended licenses reject every API request with <code>license_inactive</code>. The rows stay in the catalog and can be re-activated.</>
                    )}
                    {action.kind === "activate" && (
                        <>Sets status to <b>active</b>. Quota windows resume and API keys are accepted again.</>
                    )}
                </p>

                <div
                    style={{
                        background: "var(--color-surface-raised)",
                        borderRadius: "var(--radius-md)",
                        padding: "var(--space-3)",
                        margin: "var(--space-3) 0",
                        maxHeight: 200,
                        overflow: "auto",
                    }}
                >
                    <ul style={{ listStyle: "none", margin: 0, padding: 0, fontSize: "var(--font-size-13)" }}>
                        {action.licenses.map((l) => (
                            <li key={l.id} style={{ display: "flex", gap: 8, padding: "2px 0" }}>
                                <span className={`status-pill ${STATUS_PILL[l.status]}`} style={{ fontSize: 10, padding: "1px 6px" }}>{l.status}</span>
                                <span className="type-mono-sm">{l.name}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {requireConfirmType && (
                    <label style={{ display: "block", marginBottom: "var(--space-3)" }}>
                        <span style={{ display: "block", fontSize: "var(--font-size-12)", color: "var(--color-text-muted)", marginBottom: 4 }}>
                            Type <b>DELETE</b> to confirm
                        </span>
                        <input
                            type="text"
                            className="input"
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            autoFocus
                            spellCheck={false}
                            style={{ width: "100%", fontFamily: "var(--font-mono)" }}
                        />
                    </label>
                )}

                <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
                    <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={`btn btn--${tone === "danger" ? "danger" : "primary"}`}
                        onClick={onConfirm}
                        disabled={!canSubmit}
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {verb}{action.licenses.length === 1 ? "" : ` ${action.licenses.length}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LicensesPage(): React.ReactElement {
    const me = useCurrentUser();
    const isAdmin = me.data?.roles.includes("admin");
    const { data: licenses, isLoading, isError, error } = useLicenses();
    const tenants = useTenants();
    const [active, setActive] = useState<LicenseRecord | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
    const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
    const [busy, setBusy] = useState(false);

    const deleteMut = useDeleteLicense();
    const updateMut = useUpdateLicense();

    if (me.data && !isAdmin) {
        return <Navigate to="/" />;
    }

    function tenantLabel(id: string): string {
        const t = tenants.data?.find((x) => x.id === id);
        return t ? t.slug : id.slice(0, 8);
    }

    const activeCount = licenses?.filter((l) => l.status === "active").length ?? 0;
    const total = licenses?.length ?? 0;

    const selectedLicenses = useMemo(
        () => (licenses ?? []).filter((l) => selectedIds.has(l.id)),
        [licenses, selectedIds],
    );
    const allSelected = total > 0 && selectedIds.size === total;
    const someSelected = selectedIds.size > 0 && !allSelected;

    function toggle(id: string): void {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }
    function toggleAll(): void {
        if (allSelected || someSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set((licenses ?? []).map((l) => l.id)));
        }
    }
    function clearSelection(): void {
        setSelectedIds(new Set());
    }

    function ask(kind: ConfirmAction["kind"], rows: LicenseRecord[]): void {
        if (rows.length === 0) return;
        setConfirm({ kind, licenses: rows });
    }

    async function runConfirmed(): Promise<void> {
        if (!confirm) return;
        setBusy(true);
        let ok = 0;
        let failed = 0;
        const failedIds = new Set<string>();
        for (const lic of confirm.licenses) {
            try {
                if (confirm.kind === "delete") {
                    await deleteMut.mutateAsync(lic.id);
                } else {
                    const next: LicenseStatus = confirm.kind === "suspend" ? "suspended" : "active";
                    await updateMut.mutateAsync({ id: lic.id, status: next });
                }
                ok++;
            } catch (e) {
                failed++;
                failedIds.add(lic.id);
                // eslint-disable-next-line no-console
                console.error("license bulk action", lic.id, e);
            }
        }
        setBusy(false);
        setConfirm(null);
        if (ok > 0) {
            toast.success(
                `${actionPastTense(confirm.kind)} ${ok} license${ok === 1 ? "" : "s"}`,
            );
        }
        if (failed > 0) {
            toast.error(
                `${failed} license${failed === 1 ? "" : "s"} failed — kept selected for retry`,
            );
            setSelectedIds(failedIds);
        } else {
            clearSelection();
        }
    }

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon"><KeyRound size={14} aria-hidden /></span>
                            Identity &amp; access
                        </span>
                        <h1 className="page-header__title">Licenses</h1>
                        <p className="page-header__description">
                            Each license grants one tenant access to one product, scoped by IoC type, quota,
                            and expiry. Issue API keys from the license detail view — secrets are shown
                            exactly once.
                        </p>
                    </div>
                    <div className="page-header__actions">
                        <CreateLicenseDialog />
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item tabular-nums">
                        {total.toLocaleString()} issued
                    </span>
                    {activeCount > 0 && (
                        <span className="page-header__meta__item tabular-nums" style={{ color: "var(--color-success)" }}>
                            {activeCount} active
                        </span>
                    )}
                </div>
            </header>

            {/* Bulk action bar — shown when any row is selected. Sticky-ish at the top of the table card. */}
            {selectedIds.size > 0 && (
                <div
                    className="surface"
                    role="region"
                    aria-label="Bulk actions"
                    style={{
                        padding: "var(--space-3) var(--space-4)",
                        display: "flex",
                        gap: "var(--space-3)",
                        alignItems: "center",
                        flexWrap: "wrap",
                        borderLeft: "3px solid var(--color-accent)",
                    }}
                >
                    <span className="tabular" style={{ fontWeight: 600, fontSize: "var(--font-size-13)" }}>
                        {selectedIds.size} selected
                    </span>
                    <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => ask("activate", selectedLicenses.filter((l) => l.status !== "active"))}
                        disabled={selectedLicenses.every((l) => l.status === "active")}
                    >
                        <Play size={14} aria-hidden /> Activate
                    </button>
                    <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => ask("suspend", selectedLicenses.filter((l) => l.status === "active"))}
                        disabled={selectedLicenses.every((l) => l.status !== "active")}
                    >
                        <Pause size={14} aria-hidden /> Suspend
                    </button>
                    <button
                        type="button"
                        className="btn btn--danger"
                        onClick={() => ask("delete", selectedLicenses)}
                    >
                        <Trash2 size={14} aria-hidden /> Delete
                    </button>
                    <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={clearSelection}
                        style={{ marginLeft: "auto" }}
                    >
                        <X size={14} aria-hidden /> Clear
                    </button>
                </div>
            )}

            <section className="section-card" aria-label="Licenses">
                {isLoading && <TableSkeleton rows={6} columns={7} />}
                {isError && (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load licenses</div>
                        <div className="empty-state__body">
                            {error instanceof Error ? error.message : "Unknown error"}
                        </div>
                    </div>
                )}
                {licenses && licenses.length === 0 && (
                    <div className="empty-state">
                        <KeyRound size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No licenses yet</div>
                        <div className="empty-state__body">
                            Create one above. You'll need at least one tenant first (Tenants page).
                        </div>
                    </div>
                )}
                {licenses && licenses.length > 0 && (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <colgroup>
                                <col style={{ width: 36 }} />
                                <col />
                                <col style={{ width: 140 }} />
                                <col style={{ width: 90 }} />
                                <col style={{ width: 110 }} />
                                <col />
                                <col style={{ width: 80 }} />
                                <col style={{ width: 110 }} />
                                <col style={{ width: 60 }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={(el) => {
                                                if (el) el.indeterminate = someSelected;
                                            }}
                                            onChange={toggleAll}
                                            aria-label={allSelected ? "Deselect all" : "Select all"}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </th>
                                    <th>Name</th>
                                    <th style={{ whiteSpace: "nowrap" }}>Tenant</th>
                                    <th style={{ whiteSpace: "nowrap" }}>Product</th>
                                    <th style={{ whiteSpace: "nowrap" }}>Status</th>
                                    <th>Scope</th>
                                    <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Keys</th>
                                    <th style={{ whiteSpace: "nowrap" }}>Expires</th>
                                    <th aria-label="Row actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {licenses.map((l) => {
                                    const isSelected = selectedIds.has(l.id);
                                    return (
                                        <tr
                                            key={l.id}
                                            onClick={() => setActive(l)}
                                            style={{ cursor: "pointer" }}
                                            className={isSelected ? "is-selected" : ""}
                                        >
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggle(l.id)}
                                                    aria-label={isSelected ? "Deselect row" : "Select row"}
                                                />
                                            </td>
                                            <td className="text-13">{l.name}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                <code
                                                    className="type-mono-sm"
                                                    style={{
                                                        background: "var(--color-surface-muted)",
                                                        padding: "2px 6px",
                                                        borderRadius: "var(--radius-sm)",
                                                    }}
                                                >
                                                    {tenantLabel(l.tenantId)}
                                                </code>
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                <span className="chip">{l.productSlug}</span>
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                <span className={`status-pill ${STATUS_PILL[l.status]}`}>
                                                    {l.status}
                                                </span>
                                            </td>
                                            <td className="text-12 text-text-muted">{quotaSummary(l)}</td>
                                            <td style={{ textAlign: "right" }} className="text-13 text-text-muted tabular">
                                                {l.apiKeyCount}
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                <span className="text-12 text-text-muted">
                                                    {l.expiresAt
                                                        ? new Date(l.expiresAt).toLocaleDateString()
                                                        : "—"}
                                                </span>
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div style={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                                                    {l.status === "active" ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn--ghost"
                                                            onClick={() => ask("suspend", [l])}
                                                            title="Suspend"
                                                            aria-label={`Suspend ${l.name}`}
                                                            style={{ padding: 4 }}
                                                        >
                                                            <Pause size={14} />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn--ghost"
                                                            onClick={() => ask("activate", [l])}
                                                            title="Activate"
                                                            aria-label={`Activate ${l.name}`}
                                                            style={{ padding: 4 }}
                                                            disabled={l.status === "expired" || l.status === "revoked"}
                                                        >
                                                            {l.status === "expired" || l.status === "revoked" ? (
                                                                <Ban size={14} />
                                                            ) : (
                                                                <Play size={14} />
                                                            )}
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn--ghost"
                                                        onClick={() => ask("delete", [l])}
                                                        title="Delete"
                                                        aria-label={`Delete ${l.name}`}
                                                        style={{ padding: 4, color: "var(--color-danger)" }}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <LicenseDetailDialog license={active} onClose={() => setActive(null)} />

            <ConfirmDialog
                action={confirm}
                onCancel={() => (busy ? null : setConfirm(null))}
                onConfirm={() => void runConfirmed()}
                busy={busy}
            />
        </div>
    );
}
